import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Loading, Loader } from 'components/elements/Loading';
import GroupConfigMenu from 'components/widgets/Dashboard/GroupConfigMenu';
import Layout from 'components/widgets/Dashboard/Layout';
import EmptyStatePlaceHolder from 'components/widgets/Dashboard/ProjectGroups/EmptyStatePlaceholder';
import GroupDetails from 'components/widgets/Dashboard/ProjectGroups/GroupDetails';
import SearchFilterWizard from 'components/widgets/SearchFilterWizard';
import { useGroupConfigurations } from 'hooks/useGroupConfig';
import { useUser } from 'state/auth';
import { useDashboardGroupType } from 'state/dashboard';
import { useGroupIdQuery, useSelectedGroupQuery } from 'state/queryParams';
import { trackPageView } from 'utils/analytics';
import {
  SavedSearchesPageViewSections,
  SavedSearchesPageViewSubSections,
} from 'utils/analytics/constants';
import { GROUP_TYPE } from 'utils/constants';

type TDetailsProps = {
  isLoading: boolean;
  hasGroups: boolean;
  groupId?: string;
};

type TDetailsPropsWithWizard = TDetailsProps & {
  showWizard: boolean;
  onWizardClose: () => void;
  onWizardSuccess: (groupId: number) => void;
  isSavingSearch: boolean;
  isFirstSearch: boolean;
};

const Details = ({
  isLoading,
  hasGroups,
  groupId,
  showWizard,
  onWizardClose,
  onWizardSuccess,
  isSavingSearch,
  isFirstSearch,
}: TDetailsPropsWithWizard) => {
  // Always show wizard if it's open, even during save transition
  if (showWizard || isSavingSearch) {
    return (
      <SearchFilterWizard
        isOpen={showWizard || isSavingSearch}
        onClose={onWizardClose}
        onSuccess={onWizardSuccess}
        isFirstSearch={isFirstSearch}
      />
    );
  }
  if (isLoading) {
    return <Loader />;
  }
  // Don't show empty state if we're in the process of saving/loading a new search
  if (!hasGroups) {
    return <EmptyStatePlaceHolder />;
  }
  if (!groupId) {
    return <Loader />;
  }

  return <GroupDetails />;
};

const ProjectGroups = () => {
  const location = useLocation();
  const [, setType] = useDashboardGroupType();
  const [group] = useSelectedGroupQuery();
  const { user } = useUser();
  const [selectedGroupId, setSelectedGroupId] = useState<string>();
  const [isWizardOpen, setIsWizardOpen] = useState(false);
  const [isSavingSearch, setIsSavingSearch] = useState(false);
  const [pendingGroupId, setPendingGroupId] = useState<number | null>(null);
  const [userDismissedEmptyWizard, setUserDismissedEmptyWizard] = useState(false);
  const { setGroupIdQuery } = useGroupIdQuery();

  // Only show wizard on the project groups page (/dashboard/projects), not on project map (/projects)
  // The wizard should only show on the exact /dashboard/projects route, not on sub-routes
  const isProjectGroupsPage = location.pathname === '/dashboard/projects';

  const {
    data: groupList,
    isLoading,
    isFetching,
    refetch,
  } = useGroupConfigurations({
    userId: String(user?.id),
    groupType: GROUP_TYPE.PROJECT,
    keepPreviousData: false,
  });

  useEffect(() => {
    setType(GROUP_TYPE.PROJECT);
  }, [setType]);

  // Show wizard if user has no saved searches (only on project groups page, not map)
  useEffect(() => {
    if (!isProjectGroupsPage) {
      setIsWizardOpen(false);
      setUserDismissedEmptyWizard(false);

      return;
    }
    if (!isLoading && user && (!groupList || groupList.length === 0) && !isSavingSearch) {
      if (!userDismissedEmptyWizard) {
        setIsWizardOpen(true);
      }
    } else if (groupList && groupList.length > 0 && !isSavingSearch) {
      // Close wizard if groups are loaded (but not if we're saving)
      setIsWizardOpen(false);
      setUserDismissedEmptyWizard(false);
    }
  }, [
    groupList,
    isLoading,
    user,
    isSavingSearch,
    isProjectGroupsPage,
    userDismissedEmptyWizard,
  ]);

  const handleWizardClose = () => {
    setIsWizardOpen(false);
    if (!groupList?.length) {
      setUserDismissedEmptyWizard(true);
    }
  };

  // Complete the post-save transition only once groupList has settled with the new group
  useEffect(() => {
    if (pendingGroupId && groupList?.find(({ id }) => id === pendingGroupId)) {
      setSelectedGroupId(String(pendingGroupId));
      setGroupIdQuery?.(String(pendingGroupId));
      setPendingGroupId(null);
      setIsSavingSearch(false);
      setIsWizardOpen(false);
    }
  }, [groupList, pendingGroupId, setSelectedGroupId, setGroupIdQuery]);

  const handleWizardSuccess = (groupId: number) => {
    setIsSavingSearch(true);
    setPendingGroupId(groupId);
    refetch().catch(() => {
      setPendingGroupId(null);
      setIsSavingSearch(false);
    });
  };

  useEffect(() => {
    if (!isLoading && groupList) {
      if (groupList.length > 0 && !group) {
        // There are project groups but no group selected yet; wait for selection
        return;
      }
      const isHome = !groupList.length && !group;
      const isProjectGroup =
        group && groupList.find(({ id }) => String(id) === group)?.id;
      if (!!isHome || !!isProjectGroup) {
        const subSectionName = isHome
          ? SavedSearchesPageViewSubSections.HOME
          : String(group);

        trackPageView({
          sectionName: SavedSearchesPageViewSections.SAVED_SEARCHES,
          subSectionName,
        });
      }
    }
  }, [isLoading, group]);

  const handleGroupIdSelection = (selectedId: string) => {
    setSelectedGroupId(selectedId);
  };

  // Hide left panel if there are no saved searches
  const hasSavedSearches = groupList && groupList.length > 0;

  return (
    <Layout
      menuContent={
        hasSavedSearches ? (
          <GroupConfigMenu
            isLoading={isLoading}
            isFetching={isFetching}
            groupList={groupList}
            groupType={GROUP_TYPE.PROJECT}
            onSelectGroupId={handleGroupIdSelection}
            onNewSearchClick={() => setIsWizardOpen(true)}
          />
        ) : undefined
      }
      detailContent={
        <Loading>
          <Details
            isLoading={isLoading}
            hasGroups={!!groupList?.length}
            groupId={selectedGroupId}
            showWizard={isWizardOpen}
            onWizardClose={handleWizardClose}
            onWizardSuccess={handleWizardSuccess}
            isSavingSearch={isSavingSearch}
            isFirstSearch={!hasSavedSearches}
          />
        </Loading>
      }
    />
  );
};

export default ProjectGroups;
